import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment';
import KidSchoolKidCard from '@/components/KidSchoolKidCard.vue';
import Snackbar from '@/components/Snackbar.vue';
import { KidSchoolLive } from '@/services/KidSchoolLiveService';
import { getEmployeeId } from '@/services/BackofficeAuthService';
import { onDrained, pendingCount } from '@/services/KidSchoolQueue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
let KidSchoolAttendance = class KidSchoolAttendance extends Vue {
    loading = true;
    writingKidId = '';
    savingNoteKidId = '';
    errors = {};
    notes = {};
    employeeId = '';
    live = null;
    //<editor-fold desc="Lifecycle">
    async mounted() {
        await this.load();
        // What the queue wrote while the signal was gone is read back, so the
        // screen shows the ids and authors the backoffice gave the writes.
        onDrained(() => void this.reload());
        this.live = new KidSchoolLive({ onChange: () => this.refreshRoster() });
        await this.live.start();
    }
    beforeDestroy() {
        this.live?.stop();
    }
    //</editor-fold>
    //<editor-fold desc="Methods">
    async load() {
        try {
            // The lesson comes from the list, because the detail is merged onto a known lesson
            // and the screen can be opened before the app has loaded the day's lessons.
            if (!this.kidSchool) {
                await this.$store.dispatch('kidSchool/getAll');
            }
            await this.refreshRoster();
            await this.$store.dispatch('instructorTable/loadDates', [this.date]);
            this.employeeId = await getEmployeeId();
            await this.loadNotes();
        }
        catch (e) {
            console.error(e);
            Snackbar.error('Soupisku se nepodařilo načíst');
        }
        finally {
            this.loading = false;
        }
    }
    async reload() {
        await this.refreshRoster();
        await this.loadNotes();
    }
    async refreshRoster() {
        await this.$store.dispatch('kidSchool/getRoster', this.kidSchoolId);
    }
    async loadNotes() {
        await Promise.all(this.kids.map(async (kid) => {
            try {
                Vue.set(this.notes, kid.kidId, await this.$store.dispatch('kidSchool/getKidNotes', kid.kidId));
            }
            catch (e) {
                console.error(e);
            }
        }));
    }
    async toggleAttendance(kid, attended) {
        if (this.writingKidId) {
            return;
        }
        this.writingKidId = kid.kidId;
        Vue.delete(this.errors, kid.kidId);
        try {
            await this.$store.dispatch('kidSchool/markAttendance', {
                id: this.kidSchoolId,
                kid,
                attended,
            });
        }
        catch (e) {
            console.error(e);
            Vue.set(this.errors, kid.kidId, 'Zápis se nepovedl.');
        }
        finally {
            this.writingKidId = '';
        }
    }
    async saveNote(kid, { content, noteId }) {
        if (this.savingNoteKidId || content === '') {
            return;
        }
        this.savingNoteKidId = kid.kidId;
        try {
            const note = await this.$store.dispatch('kidSchool/saveKidNote', {
                kidId: kid.kidId,
                kidSchoolId: this.kidSchoolId,
                content,
                noteId,
                lessonId: (this.notes[kid.kidId] ?? []).find((x) => x.id === noteId)?.lessonId,
            });
            const current = this.notes[kid.kidId] ?? [];
            Vue.set(this.notes, kid.kidId, noteId
                ? current.map((x) => x.id === note.id ? note : x)
                : [note, ...current]);
        }
        catch (e) {
            console.error(e);
            Snackbar.error('Poznámku se nepodařilo uložit.');
        }
        finally {
            this.savingNoteKidId = '';
        }
    }
    async deleteNote(kid, note) {
        await ConfirmDialog.run(async () => {
            await this.$store.dispatch('kidSchool/deleteKidNote', {
                kidId: kid.kidId,
                noteId: note.id,
            });
            Vue.set(this.notes, kid.kidId, (this.notes[kid.kidId] ?? []).filter((x) => x.id !== note.id));
        }, 'Smazat poznámku', 'Opravdu smazat tuto poznámku?');
    }
    //</editor-fold>
    //<editor-fold desc="Getters">
    get queued() {
        return pendingCount();
    }
    get kidSchoolId() {
        return this.$route.params.id;
    }
    get kidSchool() {
        return this.$store.getters['kidSchool/all'].find((x) => x.id === this.kidSchoolId);
    }
    get kids() {
        return this.kidSchool?.kids ?? [];
    }
    get date() {
        return moment(this.kidSchool?.dateTime);
    }
    get timeString() {
        return this.date.format('H:mm');
    }
    get pendingKids() {
        return this.kids.filter(kid => kid.attendance !== true);
    }
    get arrivedKids() {
        return this.kids.filter(kid => kid.attendance === true);
    }
    get arrivedCount() {
        return this.arrivedKids.length;
    }
    get unpaidCount() {
        return this.kids.filter(kid => !kid.isPaid).length;
    }
    get table() {
        return this.$store.getters['instructorTable/get'](this.date);
    }
    get concluded() {
        return this.table?.concluded === true;
    }
    /** The kid school as the day's schedule knows it: who works it and who leads it. */
    get tableKidSchool() {
        return this.table?.kidSchools.find(x => x.id === this.kidSchoolId);
    }
    get isAssigned() {
        return this.tableKidSchool?.instructorIds.includes(this.$store.getters['user/userId']) === true;
    }
    get canWrite() {
        return this.isAssigned && !this.concluded;
    }
    /** A moderator corrects and removes any note, whether or not they work the lesson. */
    get canManageNotes() {
        return this.$store.getters['user/isModerator'] && !this.concluded;
    }
    /** Money is collected at the desk: the kid school leader or a moderator. */
    get canCollectPayments() {
        return this.$store.getters['user/isModerator']
            || this.tableKidSchool?.leaderId === this.$store.getters['user/userId'];
    }
    get paymentsRoute() {
        return { name: 'KidSchoolPayments', params: { id: this.kidSchoolId } };
    }
    get scheduleRoute() {
        return {
            name: 'InstructorsTimeScheduleWithDate',
            params: { date: this.date.format('YYYY-MM-DD') },
        };
    }
};
KidSchoolAttendance = __decorate([
    Component({ components: { KidSchoolKidCard } })
], KidSchoolAttendance);
export default KidSchoolAttendance;
